"use client"

import { Avatar, Button, Text } from "@/elements"
import { FragmentType, graphql, useFragment } from "@/gql"
import React, { FC } from "react"
import { ConnectOrMessageButton } from "@/components/ConnectOrMessageButton"
import { InviteToAJobDialog } from "@/components/InviteToJobDialog"
import { PlusIcon } from "@heroicons/react/24/outline"
import { usePortfolio } from "../hooks/usePortfolio"
import type { NovaSession } from "@/types"
import { PortfolioUIState } from "../page"

// NOTE: Using 390 avatar size because its the same as the main header, change if that changes
const PortfolioStickyHeaderFragment = graphql(`
  fragment PortfolioStickyHeader on IndividualCreative {
    id
    __typename

    locationName @skip(if: $displayingPrivateProfile)
    professionString
    user {
      id
      avatarInitials
      name
      profile {
        id
        avatar390w: avatarUrl(width: 390)
      }
      ...ConnectOrMessageButton @skip(if: $displayingPrivateProfile)
    }

    ...InviteToAJobDialog @skip(if: $displayingPrivateProfile)
  }
`)

interface Props {
  creative: FragmentType<typeof PortfolioStickyHeaderFragment> | undefined | null
  serverSession: NovaSession | null
  uiState: PortfolioUIState
}

export const StickyHeader: FC<Props> = props => {
  const { serverSession, uiState } = props
  const creative = useFragment(PortfolioStickyHeaderFragment, props.creative)
  const { setShowAboutModal } = usePortfolio()

  const user = creative?.user
  const name = user?.name
  const initials = user?.avatarInitials
  const avatarImg = user?.profile?.avatar390w

  const hideStickyHeader = !uiState.isAuthenticated && !uiState.isPublic

  const showHeaderActionButtons = !uiState.isUsersPortfolio && uiState.isPublic && uiState.isAuthenticated

  if (hideStickyHeader) {
    return null
  }

  return (
    <div className="z-50 flex flex-row items-start bg-bg-1 py-3 md:py-6">
      <div className="flex flex-1 flex-row flex-nowrap items-center">
        <Avatar initials={initials} src={avatarImg} size="lg" />
        <div className="ml-3 flex flex-col">
          <div className="hidden md:flex">
            <Text variant="h5">{name}</Text>
          </div>
          <div className="md:hidden">
            <Text variant="h6">{name}</Text>
          </div>
        </div>
      </div>
      <div className="flex flex-shrink-0 flex-row gap-2">
        {showHeaderActionButtons && serverSession && (
          <>
            <ConnectOrMessageButton
              user={creative?.user}
              serverSession={serverSession}
              trackingEntryPoint="portfolio-sticky-header"
            />
            <InviteToAJobDialog
              trackingEntryPoint="portfolio-sticky-header"
              creative={creative}
              trigger={<Button variant="grey-outline">Invite to Job</Button>}
            />
          </>
        )}
        <div className="ml-7 hidden md:flex">
          <div
            className="flex h-[40px] cursor-pointer flex-row items-center gap-2 rounded-md bg-bg-2 px-3 hover:bg-bg-3"
            onClick={() => setShowAboutModal(true)}>
            <Text variant="CTASmall">About</Text>
            <PlusIcon height={20} width={20} />
          </div>
        </div>
      </div>
    </div>
  )
}
